import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  ExpandMore,
  Refresh,
  PushPin,
  PushPinOutlined,
  Upload,
  LocalDining,
} from "@mui/icons-material";
import { ModelRenderer } from "../ModelRenderer";
import { AgentType, Model } from "../../models/Model";
import { MREvents } from "../MREvents";
import { PropValueLine } from "../common/PropValueLine";
import { useNavigate } from "react-router";
import { URDFRobot } from "urdf-loader";
import { Vec3Editor } from "../common/Vec3Editor";
import { ModelTreeView } from "./ModelTreeView";
import { URDFQRPropsPanel } from "./URDFQRPropsPanel";
import { QRAnimationPropsPanel } from "./QRAnimationPropsPanel";
import { UploadBlob, deleteOrgBlob } from "../common/UploadBlob";
import { OrgBlobType } from "../../models/OrgBlob";
import { GLTFAnimationPropsPanel } from "./GLTFAnimationPropsPanel";
import { CollisionMapPanel } from "./CollistionMapPanel";
import { URDFRobotArmPropsPanel } from "./URDFRobotArmPropsPanel";
import { ArmAnimationPropsPanel } from "./ArmAnimationPropsPanel";
import { PartsPanel } from "./PartsPanel";
import { saveModel } from "../../api/connection";
import { RenderAccordionToolbar } from "../../components/Minibar";

export function ModelPropertiesPanel({ mr }: { mr: ModelRenderer }) {
  const theme = useTheme();
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [urdfRobot, setUrdfRobot] = useState<URDFRobot>();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mr) return;
    const handleModelSelected = (model: Model) => {
      mr.loader
        .loadModel(model)
        .then(() => setUrdfRobot(mr.loader.robot))
        .catch(console.error);

      setSelectedModel(model);
      if (!model) return;

      if (model.settings.cameraPosition) {
        mr.setCameraPosition(model.settings.cameraPosition);
      }
    };
    mr.onEvent(MREvents.ModelSelected, handleModelSelected);
    return () => {
      mr.removeListener(MREvents.ModelSelected, handleModelSelected);
    };
  }, [mr]);

  if (!selectedModel) {
    return (
      <Box sx={{ padding: theme.spacing(2) }}>
        <Typography variant="h6">LOADING</Typography>
      </Box>
    );
  }

  const merge = (update: Partial<Model>) => {
    setSelectedModel({ ...selectedModel, ...update });
    Object.assign(mr.loader.model, update);
    mr.loader.adapt();
  };

  const toggleExpand = (panel: string) => {
    setExpanded(expanded === panel ? false : panel);
  };

  const togglePin = (panel: string) => {
    setPinned((prev) => {
      const next = new Set(prev);
      next.has(panel) ? next.delete(panel) : next.add(panel);
      return next;
    });
  };

  const isOpen = (panel: string) => expanded === panel || pinned.has(panel);

  const renderAccordionSummary = (
    panel: string,
    label: string,
    variant: "h6" | "body2" = "body2"
  ) => (
    <AccordionSummary
      expandIcon={<ExpandMore />}
      onClick={() => toggleExpand(panel)}
    >
      <Typography variant={variant} sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      {/* <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          togglePin(panel);
        }}
      >
        {pinned.has(panel) ? (
          <PushPin fontSize="inherit" />
        ) : (
          <PushPinOutlined fontSize="inherit" />
        )}
      </IconButton> */}
    </AccordionSummary>
  );

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {selectedModel && (
        <>
          {/* Model Info */}
          <Accordion disableGutters expanded={isOpen("model")}>
            {renderAccordionSummary(
              "model",
              `Model: ${selectedModel.category} / ${selectedModel.name}`,
              "h6"
            )}
            <AccordionDetails>
              <RenderAccordionToolbar
                pinned={pinned}
                panel={"model"}
                togglePin={togglePin}
              />
              <PropValueLine
                label="Category"
                value={
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedModel.category}
                    onChange={(e) => merge({ category: e.target.value })}
                    variant="standard"
                  />
                }
              />
              <PropValueLine
                label="Name"
                value={
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedModel.name}
                    onChange={(e) => merge({ name: e.target.value })}
                    variant="standard"
                  />
                }
              />
              <PropValueLine
                label="Agent type"
                value={
                  <TextField
                    select
                    fullWidth
                    variant="standard"
                    size="small"
                    value={selectedModel.agentType}
                    onChange={(e) =>
                      merge({ agentType: e.target.value as any })
                    }
                  >
                    {Object.values(AgentType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Source */}
          <Accordion disableGutters expanded={isOpen("source")}>
            {renderAccordionSummary("source", "Source")}
            <AccordionDetails>
              <PropValueLine
                label="URDF URL"
                value={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      value={selectedModel.source.url || ""}
                      onChange={(e) => {
                        merge({
                          source: {
                            ...selectedModel.source,
                            url: e.target.value,
                          },
                        });
                      }}
                      variant="standard"
                    />

                    <IconButton
                      sx={{ ml: theme.spacing(1) }}
                      color="secondary"
                      onClick={async () => {
                        if (selectedModel.source.url) {
                          await mr.loader.loadModel(selectedModel);
                        }
                      }}
                    >
                      <Refresh />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={async () => {
                        if (selectedModel.source.url) {
                          setLoading(true);
                          if (selectedModel.source.blobId) {
                            // Delete the old blob
                            await deleteOrgBlob(selectedModel.source.blobId);
                          }
                          const blobId = await mr.loader.packageUrdfToBlob(
                            selectedModel
                          );
                          console.log({ blobId });
                          merge({
                            source: {
                              ...selectedModel.source,
                              blobId,
                            },
                          });
                          await mr.loader.loadURDFModelFromBlobStorage(
                            selectedModel
                          );
                          await saveModel(selectedModel);
                          setLoading(false);
                        }
                      }}
                    >
                      <Upload />
                    </IconButton>
                  </div>
                }
              />
              {/* <PropValueLine
                label="PACKAGE ROOT"
                value={
                  <TextField
                    size="small"
                    value={selectedModel.source.packageRoot || ""}
                    onChange={(e) => {
                      merge({
                        source: {
                          ...selectedModel.source,
                          packageRoot: e.target.value,
                        },
                      });
                    }}
                    variant="standard"
                  />
                }
              /> */}
              <PropValueLine
                label="Blob ID"
                value={
                  <UploadBlob
                    blobId={selectedModel.source.blobId || ""}
                    onUploaded={(blobId) => {
                      merge({
                        source: {
                          ...selectedModel.source,
                          blobId,
                        },
                      });
                      mr.loader.loadModel(selectedModel);
                    }}
                  />
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Adjustment */}
          <Accordion disableGutters expanded={isOpen("adjustment")}>
            {renderAccordionSummary("adjustment", "Adjustment")}
            <AccordionDetails>
              <PropValueLine
                label="Position"
                value={
                  <Vec3Editor
                    value={selectedModel.adaptation.position}
                    onChange={(value) =>
                      merge({
                        adaptation: {
                          ...selectedModel.adaptation,
                          position: value,
                        },
                      })
                    }
                  />
                }
              />
              <PropValueLine
                label="Rotation"
                value={
                  <Vec3Editor
                    convertToDegrees
                    value={selectedModel.adaptation.rotation}
                    onChange={(value) =>
                      merge({
                        adaptation: {
                          ...selectedModel.adaptation,
                          rotation: value,
                        },
                      })
                    }
                  />
                }
              />
              <PropValueLine
                label="Scale"
                value={
                  <TextField
                    type="number"
                    value={selectedModel.adaptation.scale}
                    onChange={(e) =>
                      merge({
                        adaptation: {
                          ...selectedModel.adaptation,
                          scale: parseFloat(e.target.value),
                        },
                      })
                    }
                    variant="standard"
                    size="small"
                  />
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Object Tree */}
          <Accordion disableGutters expanded={isOpen("tree")}>
            {renderAccordionSummary("tree", "Object Tree")}
            <AccordionDetails>
              <ModelTreeView mr={mr} />
            </AccordionDetails>
          </Accordion>

          {/* Robot Specific Panels */}
          {urdfRobot && selectedModel.agentType === AgentType.RobotArm && (
            <Accordion disableGutters expanded={isOpen("robotArm")}>
              {renderAccordionSummary("robotArm", "Robot Arm URDF")}
              <AccordionDetails>
                <URDFRobotArmPropsPanel mr={mr} robot={urdfRobot} />
              </AccordionDetails>
            </Accordion>
          )}

          {urdfRobot && selectedModel.agentType === AgentType.Robot && (
            <Accordion disableGutters expanded={isOpen("quadruped")}>
              {renderAccordionSummary("quadruped", "Quadruped URDF")}
              <AccordionDetails>
                <URDFQRPropsPanel mr={mr} robot={urdfRobot} />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Parts */}
          <Accordion disableGutters expanded={isOpen("parts")}>
            {renderAccordionSummary("parts", "Parts")}
            <AccordionDetails>
              <PartsPanel mr={mr} />
            </AccordionDetails>
          </Accordion>

          {/* Animations */}
          <Accordion disableGutters expanded={isOpen("animations")}>
            {renderAccordionSummary("animations", "Animations")}
            <AccordionDetails>
              {urdfRobot && selectedModel.agentType === AgentType.Robot && (
                <QRAnimationPropsPanel mr={mr} robot={urdfRobot} />
              )}
              {urdfRobot && selectedModel.agentType === AgentType.RobotArm && (
                <ArmAnimationPropsPanel mr={mr} robot={urdfRobot} />
              )}
              {!urdfRobot && <GLTFAnimationPropsPanel mr={mr} />}
            </AccordionDetails>
          </Accordion>

          {/* Collision Map */}
          <Accordion disableGutters expanded={isOpen("collision")}>
            {renderAccordionSummary("collision", "Collision Map")}
            <AccordionDetails>
              <CollisionMapPanel mr={mr} />
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
